import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Typography, Spin } from 'antd';
import { SafetyCertificateOutlined, CloseCircleOutlined, WarningOutlined, ClockCircleOutlined, InfoCircleOutlined, MailOutlined, CheckCircleOutlined } from '@ant-design/icons';
import podsLogo from '@assets/pods_logo_1.png';
import CheckCircleThin from '@assets/check-circle-thin.png';
import makeApiRequest from '@apiCalls/makeApiRequest';
import { API_ENDPOINTS, MY_PODS_URL } from '@constants/apiEndpoints';
import { useNotification } from '@providers/NotificationProviders';
import type apiRequestConfig from '@interfaces/apiEndpoints.interface';
import * as styles from '@styles/militaryVerification.module.scss';
import moment from 'moment';

const { Title, Text } = Typography;

interface VerificationData {
    verificationId: string;
    customerId: string;
    orderId: string;
    customerEmail: string;
    status: 'PENDING' | 'VERIFIED' | 'EXPIRED' | 'FAILED';
    authorizationUrl?: string;
    expiresAt: number;
    isExpired: boolean;
    errorMessage?: string;
}

const MilitaryVerification: React.FC = () => {
    const { verificationId } = useParams<{ verificationId: string }>();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<VerificationData | null>(null);
    const [regenerating, setRegenerating] = useState(false);
    const [regenerated, setRegenerated] = useState(false);
    const [countdown, setCountdown] = useState(15);
    const openNotification = useNotification();

    const processedRef = React.useRef(false);

    useEffect(() => {
        if (verificationId && !processedRef.current) {
            processedRef.current = true;
            fetchVerificationStatus();
        }
    }, [verificationId]);

    useEffect(() => {
        const isVerified = data?.status === 'VERIFIED';
        const isPending = data?.status === 'FAILED' && data?.errorMessage?.toLowerCase().includes('pending');

        if ((isVerified || isPending) && countdown > 0) {
            const timer = setTimeout(() => {
                setCountdown(countdown - 1);
            }, 1000);
            return () => clearTimeout(timer);
        } else if ((isVerified || isPending) && countdown === 0) {
            window.location.href = MY_PODS_URL;
        }
    }, [data, countdown]);

    const fetchVerificationStatus = () => {
        setLoading(true);
        const requestConfig = API_ENDPOINTS.GET_MILITARY_VERIFICATION as apiRequestConfig;

        makeApiRequest(
            {},
            requestConfig,
            verificationId! // Pass verificationId here
        )
            .then((res) => {
                if (res.status === 200) {
                    setData(res.data as VerificationData);
                } else {
                    openNotification('Failed to fetch verification status', 'Error');
                }
            })
            .catch((err) => {
                openNotification(err.response?.data?.message || 'Error fetching status', 'Error');
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleAuthorize = () => {
        if (data?.authorizationUrl) {
            window.location.href = data.authorizationUrl;
        }
    };

    const handleRegenerateLink = () => {
        setRegenerating(true);
        const requestConfig = API_ENDPOINTS.REGENERATE_MILITARY_VERIFICATION as apiRequestConfig;

        makeApiRequest(
            {},
            requestConfig,
            verificationId!
        )
            .then((res) => {
                if (res.status === 200) {
                    setRegenerated(true);
                    openNotification('Verification link regenerated successfully! Please check your email.', 'Success');
                } else {
                    openNotification('Failed to regenerate verification link', 'Error');
                }
            })
            .catch((err) => {
                openNotification(err.response?.data?.message || 'Error regenerating link', 'Error');
            })
            .finally(() => {
                setRegenerating(false);
            });
    };

    const maskEmail = (email: string) => {
        if (!email) return '';
        const [name, domain] = email.split('@');
        if (name.length <= 2) return `${name}***@${domain}`;
        return `${name.substring(0, 2)}***@${domain}`;
    };

    const maskCustomerId = (id: string) => {
        // Assuming format like C-123456. User said "ok masked".
        // Let's just obscure the middle part.
        if (!id || id.length < 5) return id;
        return `${id.substring(0, 2)}***${id.substring(id.length - 2)}`;
    };

    if (loading) {
        return (
            <section className={styles.loginBody}>
                <Spin size="large" />
            </section>
        );
    }

    if (!data) {
        return (
            <section className={styles.loginBody}>
                <div className={styles.logoWeb}>
                    <img src={podsLogo as any} alt="PODS Logo" />
                </div>
                <section className={styles.formLogin}>
                    <Title level={3}>Verification not found</Title>
                </section>
            </section>
        );
    }

    return (
        <section className={styles.loginBody}>
            <div className={styles.logoWeb}>
                <img src={podsLogo as any} alt="PODS Logo" />
            </div>

            <section className={styles.formLogin}>
                {data.status === 'VERIFIED' ? (
                    <div className={styles.errorContainer} style={{ minHeight: 'auto', background: 'transparent' }}>
                        <div className={styles.errorCard} style={{ boxShadow: 'none', padding: 0 }}>
                            <div className={styles.verifiedIcon}>
                                <CheckCircleOutlined />
                            </div>
                            <h2 className={styles.reviewTitle}>
                                Your GovX Verification is <br /> Successful
                            </h2>
                            <div className={styles.reviewSubtitle}>
                                Your military discount has been secured.
                                Please login to MyPODS to manage your order.
                            </div>
                            <Button
                                type="primary"
                                className={styles.manageOrdersBtn}
                                onClick={() => window.location.href = MY_PODS_URL}
                            >
                                Manage order in MyPODS
                            </Button>
                            <div className={styles.redirectText}>
                                You will be automatically redirected to MyPODS in {countdown} seconds.
                            </div>
                        </div>
                    </div>
                ) : data.status === 'FAILED' ? (() => {
                    let errorText = 'Unknown error occurred';
                    try {
                        const parsed = JSON.parse(data.errorMessage || '{}');
                        if (parsed.error && parsed.expected_email && parsed.authenticated_email) {
                            errorText = `Email mismatch. Expected ${parsed.expected_email} but authenticated with ${parsed.authenticated_email}`;
                        } else {
                            errorText = data.errorMessage || 'Unknown error occurred';
                        }
                    } catch (e) {
                        errorText = data.errorMessage || 'Unknown error occurred';
                    }

                    const isPendingError = errorText.toLowerCase().includes('pending');

                    return (
                        <div className={styles.errorContainer} style={{ minHeight: 'auto', background: 'transparent' }}>
                            <div className={styles.errorCard} style={{ boxShadow: 'none', padding: 0 }}>
                                {isPendingError ? (
                                    <>
                                        <div className={styles.reviewIcon}>
                                            <ClockCircleOutlined />
                                        </div>
                                        <h2 className={styles.reviewTitle}>
                                            Your GovX Verification is <br /> Under Review
                                        </h2>
                                        <div className={styles.reviewSubtitle}>
                                            You will receive your approval email from GovX within the next 24 hours.
                                            In the meantime, please login to MyPODS to manage your order.
                                        </div>
                                        <Button
                                            type="primary"
                                            className={styles.manageOrdersBtn}
                                            onClick={() => window.location.href = MY_PODS_URL}
                                        >
                                            Manage order in MyPODS
                                        </Button>
                                        <div className={styles.redirectText}>
                                            You will be automatically redirected to MyPODS in {countdown} seconds.
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className={styles.errorIcon}>
                                            <CloseCircleOutlined />
                                        </div>
                                        <h2 className={styles.errorTitle}>Verification Failed</h2>
                                        <div style={{ marginBottom: '24px', textAlign: 'center', width: '100%' }}>
                                            <div className={styles.errorDetailsBox}>
                                                <div className={styles.errorDetailsTitle}>
                                                    <WarningOutlined className={styles.errorDetailsIcon} />
                                                    Error Details
                                                </div>
                                                <div className={styles.errorMessage}>
                                                    {errorText}
                                                </div>
                                            </div>
                                            <div style={{ fontSize: '14px', color: '#666', marginTop: '16px' }}>
                                                To manage your orders visit <a href={MY_PODS_URL} target="_blank" rel="noopener noreferrer" style={{ color: '#297ed1', fontWeight: 600 }}>mypods.com</a>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {!isPendingError && (
                                    <div className={styles.commonReasonsBox}>
                                        <div className={styles.commonReasonsTitle}>Common reasons:</div>
                                        <ul className={styles.commonReasonsList}>
                                            <li>Email mismatch (authenticated with different email)</li>
                                            <li>Authentication session timed out</li>
                                            <li>Technical error with the verification service</li>
                                        </ul>
                                    </div>
                                )}

                                {!isPendingError && (
                                    <div style={{ display: 'flex', gap: '12px', marginTop: '8px', width: '100%', justifyContent: 'center' }}>
                                        <Button
                                            type="default"
                                            className={styles.contactSupportBtn}
                                            onClick={() => window.open('https://www.pods.com/contact-us', '_blank')}
                                        >
                                            Contact Support
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })() : (
                    <>
                        <div className={styles.govxIconContainer}>
                            <SafetyCertificateOutlined />
                        </div>

                        <Title className={styles.govxTitle}>
                            Verify Your Military Status
                        </Title>

                        <div className={styles.govxSubtitle}>
                            Thank you for your service. Complete this quick verification to confirm your military discount eligibility.
                        </div>

                        <Button
                            className={styles.govxVerifyBtn}
                            type="primary"
                            onClick={handleAuthorize}
                            disabled={data.isExpired}
                        >
                            Continue to GovX
                        </Button>

                        <div className={styles.govxFooterNote}>
                            You'll be redirected to GovX to verify your military status. This process is secure and takes less than 2 minutes.
                        </div>
                    </>
                )}
            </section>
        </section >
    );
};

export default MilitaryVerification;
